import type {
  IStateMachineOfTaskAssignmentStateEnum,
  IStateMachineOfTaskDefinitionStateEnum,
  TaskAssignmentResponse,
  TaskDefinitionRequest,
  TaskDefinitionResponse,
  TaskOfflineBundleResponse,
  TransitionRequest,
} from 'src/api/ProactApiClient';

// The API client may only be imported by src/redux and src/offline, so the task
// replay calls are injected; see src/redux/queries/tasksOfflineRegistration.ts.
export interface TasksOfflineApi {
  getOfflineBundle: () => Promise<TaskOfflineBundleResponse>;
  getAssignment: (id: number) => Promise<TaskAssignmentResponse>;
  transitionAssignment: (
    id: number,
    request: TransitionRequest
  ) => Promise<TaskAssignmentResponse>;
  getAssignmentStateMachine: () => Promise<IStateMachineOfTaskAssignmentStateEnum>;
  getDefinitionStateMachine: () => Promise<IStateMachineOfTaskDefinitionStateEnum>;
  createDefinition: (
    request: TaskDefinitionRequest
  ) => Promise<TaskDefinitionResponse>;
  getDefinition: (id: number) => Promise<TaskDefinitionResponse>;
  transitionDefinition: (
    id: number,
    request: TransitionRequest
  ) => Promise<TaskDefinitionResponse>;
}

let boundApi: TasksOfflineApi | undefined;

export const bindTasksOfflineApi = (api: TasksOfflineApi): void => {
  boundApi = api;
};

const api = (): TasksOfflineApi => {
  if (!boundApi) {
    throw new Error(
      'Tasks offline API is not bound — import src/redux/queries/tasksOfflineRegistration first.'
    );
  }
  return boundApi;
};

export const getOfflineBundle = (): Promise<TaskOfflineBundleResponse> =>
  api().getOfflineBundle();

export const getAssignment = (id: number): Promise<TaskAssignmentResponse> =>
  api().getAssignment(id);

export const transitionAssignment = (
  id: number,
  request: TransitionRequest
): Promise<TaskAssignmentResponse> => api().transitionAssignment(id, request);

export const getAssignmentStateMachine =
  (): Promise<IStateMachineOfTaskAssignmentStateEnum> =>
    api().getAssignmentStateMachine();

export const getDefinitionStateMachine =
  (): Promise<IStateMachineOfTaskDefinitionStateEnum> =>
    api().getDefinitionStateMachine();

export const createDefinition = (
  request: TaskDefinitionRequest
): Promise<TaskDefinitionResponse> => api().createDefinition(request);

export const getDefinition = (id: number): Promise<TaskDefinitionResponse> =>
  api().getDefinition(id);

export const transitionDefinition = (
  id: number,
  request: TransitionRequest
): Promise<TaskDefinitionResponse> => api().transitionDefinition(id, request);
